// sentry-http.interceptor.ts
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

@Injectable()
export class SentryHttpInterceptor implements HttpInterceptor {
  // e.g. ignore auth and not-found noise
  private readonly _ignoreStatuses = new Set([401, 404]);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // optional breadcrumb so successful/failed requests appear in the trail
    Sentry.addBreadcrumb({
      category: 'http',
      type: 'http',
      data: { method: req.method, url: req.urlWithParams },
    });
    if (req.url.includes('/login')) {
      return next.handle(req).pipe(catchError((err) => throwError(() => err)));
    }
    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse) {
          // Filter noisy statuses if you want
          if (!this._ignoreStatuses.has(err.status)) {
            // Create a clean error object + context (avoid dumping giant bodies/PII)
            const error = new Error(`HTTP ${err.status} ${err.statusText} for ${req.method} ${req.url}`);
            Sentry.captureException(error, {
              level: 'error',
              tags: {
                http_method: req.method,
                http_status: String(err.status),
              },
              extra: {
                request: {
                  url: req.url,
                  method: req.method,
                },
                response: {
                  status: err.status,
                  statusText: err.statusText,
                  // include a safe slice of the body if useful:
                  bodyPreview:
                    typeof err.error === 'string' ? err.error.slice(0, 500) : JSON.stringify(err.error)?.slice(0, 500),
                },
              },
            });
          }
        } else {
          // Non-HTTP error in the pipeline
          Sentry.captureException(err);
        }
        return throwError(() => err);
      }),
    );
  }
}
